import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { DomSanitizer, SafeStyle } from "@angular/platform-browser";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, merge, Observable, of, Subject } from "rxjs";
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    mapTo,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
} from "rxjs/operators";

import {
    GetCollectionQuery,
    GetCollectionQueryVariables,
    SearchProductsQuery,
    SearchProductsQueryVariables,
    SearchTotalCountQuery,
    SearchTotalCountQueryVariables,
    AddToCartMutation,
    AddToCartMutationVariables,
    GetActiveCustomerQuery,
} from "../../../common/generated-types";
import { getRouteArrayParam } from "../../../common/utils/get-route-array-param";
import { buildProductRoute } from "../../../common/utils/build-product-route";
import { AssetPreviewPipe } from "../../../shared/pipes/asset-preview.pipe";
import { isSupplierNoImagePlaceholder } from "../../../shared/utils/product-image.utils";
import { DataService } from "../../providers/data/data.service";
import { StateService } from "../../providers/state/state.service";
import { NotificationService } from "../../providers/notification/notification.service";
import { ModalService } from "../../providers/modal/modal.service";
import { ExportDownloadService } from "../../providers/export-download/export-download.service";
import { ProductListViewCacheService } from "../../providers/product-list-view-cache/product-list-view-cache.service";
import { CartUploadService } from "../../providers/cart-upload/cart-upload.service";
import {
    CatalogExportModalComponent,
    CatalogExportModalResult,
} from "../../../shared/components/catalog-export-modal/catalog-export-modal.component";
import {
    CatalogExportFilters,
    ExportFormat,
    SHARE_LINK_ERROR_PREFIX,
    SHARE_LINK_SUCCESS,
} from "../../../shared/types/export-format.types";
import { SUPPLY_TYPE } from "../../../shared/constants/pricelist.constants";
import {
    extractEanCodesFromText,
    normalizeEanSearchTerm,
} from "../../../shared/utils/parse-ean-list";
import { environment } from "../../../../environments/environment";

import {
    GET_COLLECTION,
    SEARCH_PRODUCTS,
    SEARCH_TOTAL_COUNT,
    ADD_TO_CART,
    GET_SUPPLIER_INFO,
    GET_ACTIVE_ORDER,
    GET_ACTIVE_CUSTOMER,
    SEARCH_FACET_VALUES,
    GET_PRODUCT_RETAIL_PRICE_RANGES,
    GET_STOREFRONT_CONSOLIDATED_FEED,
} from "./product-list.graphql";
import {
    ActiveFilter,
    ProductListBreadcrumb,
    SortOption,
} from "./components/product-list-header/product-list-header.types";
import { PRICELIST_CARD_TEXT, PRICELIST_TEXT } from "../../../shared/constants/pricelist.constants";
import { AVAILABILITY_TEXT } from "../../../shared/constants/availability.constants";
import {
    getMoq,
    getMinQ,
    getMaxQ,
    getQuantityInfoText,
    hasMinQ,
    getOrderStep,
    getBundleLabel,
    getInitialQuantity,
    normalizePackageQuantity,
    PackageProductContext,
} from "../../../shared/utils/pricing.utils";
import { DateUtils } from "../../../shared/utils/date.utils";
import {
    buildProductListRouteCommands,
    getClearProductListFilterQueryParams,
    getSupplierDeliveryTerm,
    showSupplierPrePayment,
    getRemainingToMinimumOrder,
    isMinimumOrderMet,
    getProgressPercentage,
    getProgressColor,
    isSupplierPreOrder,
    getSearchInput,
} from "./product-list.utils";
import {
    FACET_SEARCH_ROUTE_QUERY_PARAM,
    LEGACY_SEARCH_QUERY_PARAM,
    MOV_FILTER_QUERY_PARAM,
    ORDER_QUERY_PARAM,
    PAGE_QUERY_PARAM,
    PER_PAGE_QUERY_PARAM,
    PRODUCT_LIST_SIDEBAR_STORAGE_KEY,
    SHIPPING_DATE_FILTER_QUERY_PARAM,
    SUPPLY_TYPE_FILTER_QUERY_PARAM,
    SORT_QUERY_PARAM,
    supportsSupplyTypeFilter,
    CONSOLIDATED_FEED_CHECKOUT_MOV_CENTS,
    CONSOLIDATED_FEED_DEFAULT_CHECKOUT_MOV_CENTS,
    CONSOLIDATED_FEED_DEFAULT_PREVIEW_MOV_CENTS,
    CONSOLIDATED_FEED_MOV_TIERS_CENTS,
    CONSOLIDATED_FEED_ETA,
} from "./product-list.constants";
import { filterVariantsBySupplyTypes } from "../../../common/utils/supply-type-filter.utils";
import { ConsolidatedFeedStorefrontService } from "../../../common/services/consolidated-feed-storefront.service";
import { syncConsolidatedFeedOrderFlagFromActiveOrder } from "../../../common/utils/mark-consolidated-feed-order";
import { isConsolidatedFeedOrder } from "../../../common/utils/is-consolidated-feed-order";
import {
    filterVariantsWithValidSupplier,
    isValidSupplierId,
    pickCheapestOffer,
} from "../../../common/utils/variant-offer.utils";
import {
    PRODUCTS_COLLECTION_SLUG,
    brandSlugToFacetCode,
    brandSlugToFilterTerm,
    slugifyBrandName,
} from "../brands-page/brands-page.constants";
import { GET_CATALOG_BRANDS } from "../brands-page/brands-page.graphql";

import {
    SupplierInfo,
    GetSupplierInfoResult,
    ActiveOrderLine,
    ActiveOrder,
    GetActiveOrderResult,
    SearchFacetValuesQuery,
    SearchFacetValuesQueryVariables,
    SearchItem,
    ProductListVariant,
    EnhancedSearchItem
} from "./product-list.types";

@Component({
    selector: "vsf-product-list",
    templateUrl: "./product-list.component.html",
    styleUrls: ["./product-list.component.scss"],
})
export class ProductListComponent implements OnInit, OnDestroy {
    TEXT = AVAILABILITY_TEXT;
    products$: Observable<EnhancedSearchItem[]>;
    totalResults$: Observable<number>;
    collection$: Observable<GetCollectionQuery["collection"]>;
    facetValues: SearchProductsQuery["search"]["facetValues"];
    unfilteredTotalItems = 0;
    activeFacetValueIds$: Observable<string[]>;
    searchTerm$: Observable<string>;
    currentPage$: Observable<number>;
    totalPages$: Observable<number>;
    perPage$: Observable<number>;
    loading$: Observable<boolean>;
    canGoNext$: Observable<boolean>;
    canGoPrevious$: Observable<boolean>;
    breadcrumbs$: Observable<ProductListBreadcrumb[]>;
    mastheadBackground$: Observable<SafeStyle>;
    activeCustomer$: Observable<GetActiveCustomerQuery["activeCustomer"]>;
    private refresh = new BehaviorSubject<void>(undefined);
    readonly placeholderProducts = Array.from({ length: 12 }).map(() => null);
    itemQuantities: { [productId: string]: number } = {};
    retailPriceRanges: {
        [productId: string]: { min: number | null; max: number | null; currency: string | null };
    } = {};
    supplierId: string | null = null;
    isConsolidatedFeed = false;
    consolidatedFeedPricingIds: number[] = [];
    consolidatedFeedLoaded = false;
    consolidatedFeedLiveUrls: {
        csv?: string | null;
        xlsx?: string | null;
        xml?: string | null;
    } | null = null;
    consolidatedFeedCustomerMovCents: number | null = null;
    readonly consolidatedFeedEta = CONSOLIDATED_FEED_ETA;
    readonly consolidatedFeedMovTiers = CONSOLIDATED_FEED_MOV_TIERS_CENTS;
    selectedConsolidatedMovTierCents: number = CONSOLIDATED_FEED_DEFAULT_PREVIEW_MOV_CENTS;
    consolidatedFeedSearchTerm = "";
    private readonly consolidatedFeedPricingIds$ = new BehaviorSubject<number[] | null>(null);
    collectionSlug: string | null = null;
    brandSlug: string | null = null;
    brandDisplayName: string | null = null;
    private latestTotalResults = 0;
    private latestCollection: GetCollectionQuery["collection"] | undefined;
    private activeFacetValueIdsFromBrand: string | null = null;
    isLoading = false;
    failedProductGtins: string[] = [];
    moqViolations: Array<{ gtin: string; moq: number; quantity: number }> = [];
    supplierInfo: SupplierInfo | null = null;
    activeOrder: ActiveOrder | null = null;
    supplierSpecificTotal = 0;
    filteredOrderLines: ActiveOrderLine[] = [];
    hasLoadedData$: Observable<boolean>;
    filterModalVisibility = false;
    csvFileStatus: { exists: boolean; lastModified: Date | null } | null = null;
    private cartUpdate = new BehaviorSubject<void>(undefined);
    readonly perPage = 25;
    isPopupOpen = false;
    perPageDropdownOpen = false;
    showSidebar = true;
    bulkBuyOpen = true;
    bulkBuyPasteText = "";
    bulkBuyPasteSubmitting = false;
    sortField: string | null = null;
    sortOrder: 'ASC' | 'DESC' | null = null;
    mobileMenuOpen = false;
    isMobile = false;

    viewMode: 'grid' | 'table' = 'grid';
    selectedSupplierTierMinCents: number | null = null;
    supplierPricelistSearchTerm = '';
    addingAllSupplierProducts = false;
    selectedSupplierProductIds = new Set<string>();
    private latestSupplierPageProducts: EnhancedSearchItem[] = [];

    @HostListener('window:resize')
    onResize() {
        this.checkViewport();
    }

    private checkViewport() {
        if (typeof window !== 'undefined') {
            this.isMobile = window.innerWidth <= 800;
            if (this.supplierId) {
                this.viewMode = 'table';
                return;
            }
            if (this.isSearchResultsPage || this.isMobile) {
                this.viewMode = 'grid';
            }
        }
    }

    onSelectSupplierTier(minCents: number): void {
        this.selectedSupplierTierMinCents = minCents;
        this.cdr.markForCheck();
    }

    getSupplierCartUnitCount(): number {
        return this.filteredOrderLines.reduce(
            (sum, line) => sum + (line.quantity || 0),
            0
        );
    }

    getSupplierStickyMessage(): string {
        const mov = Number(this.supplierInfo?.mov) || 0;
        if (!mov) {
            return this.supplierSpecificTotal > 0
                ? 'Continue adding products'
                : 'Add products from this pricelist';
        }
        if (this.isMinimumOrderMet()) {
            return `MOV reached · ${this.formatStickyPrice(mov)}`;
        }
        const remaining = Math.max(mov - this.supplierSpecificTotal, 0);
        return `Add ${this.formatStickyPrice(remaining)} more to reach the ${this.formatStickyPrice(mov)} MOV`;
    }

    get selectedSupplierProductCount(): number {
        return this.latestSupplierPageProducts.filter((product) =>
            this.selectedSupplierProductIds.has(String(product.productId))
        ).length;
    }

    get allVisibleSupplierSelected(): boolean {
        const products = this.latestSupplierPageProducts;
        return (
            products.length > 0 &&
            products.every((product) =>
                this.selectedSupplierProductIds.has(String(product.productId))
            )
        );
    }

    rememberSupplierPageProducts(products: EnhancedSearchItem[]): EnhancedSearchItem[] {
        this.latestSupplierPageProducts = products || [];
        return products;
    }

    isSupplierProductSelected(productId: string | number): boolean {
        return this.selectedSupplierProductIds.has(String(productId));
    }

    toggleSupplierProductSelected(productId: string | number, event: Event): void {
        const checked = !!(event.target as HTMLInputElement | null)?.checked;
        const key = String(productId);
        if (checked) {
            this.selectedSupplierProductIds.add(key);
        } else {
            this.selectedSupplierProductIds.delete(key);
        }
        this.cdr.markForCheck();
    }

    toggleSelectAllVisibleSupplierProducts(event: Event): void {
        const checked = !!(event.target as HTMLInputElement | null)?.checked;
        if (checked) {
            for (const product of this.latestSupplierPageProducts) {
                this.selectedSupplierProductIds.add(String(product.productId));
            }
        } else {
            for (const product of this.latestSupplierPageProducts) {
                this.selectedSupplierProductIds.delete(String(product.productId));
            }
        }
        this.cdr.markForCheck();
    }

    onSupplierPricelistSearchInput(): void {
        const term = this.supplierPricelistSearchTerm.trim();
        const activeTerm = readSearchTermParam(this.route.snapshot.queryParamMap);
        if (!term && activeTerm) {
            this.setConsolidatedFeedSearchParam(null);
        }
    }

    submitSupplierPricelistSearch(): void {
        const term = this.supplierPricelistSearchTerm.trim();
        this.setConsolidatedFeedSearchParam(term || null);
    }

    async addSelectedSupplierProductsToCart(): Promise<void> {
        if (this.addingAllSupplierProducts) {
            return;
        }
        const selected = this.latestSupplierPageProducts.filter((product) =>
            this.selectedSupplierProductIds.has(String(product.productId))
        );
        if (!selected.length) {
            this.notificationService.error('Select products to add to cart.').subscribe();
            return;
        }
        this.addingAllSupplierProducts = true;
        this.cdr.markForCheck();
        let added = 0;
        try {
            for (const product of selected) {
                const variantId = product.cheapestVariant?.id;
                if (!variantId) {
                    continue;
                }
                const quantity = this.getQuantity(
                    product.productId,
                    product.cheapestVariant,
                    product
                );
                await new Promise<void>((resolve) => {
                    this.dataService
                        .mutate<AddToCartMutation, AddToCartMutationVariables>(
                            ADD_TO_CART,
                            {
                                variantId,
                                qty: quantity,
                            } as AddToCartMutationVariables
                        )
                        .subscribe({
                            next: ({ addItemToOrder }) => {
                                if (addItemToOrder.__typename === 'Order') {
                                    added += 1;
                                }
                                resolve();
                            },
                            error: () => resolve(),
                        });
                });
            }
            this.cartUpdate.next();
            this.notificationService
                .success(`Added ${added} product${added === 1 ? '' : 's'} to the cart.`)
                .subscribe();
        } finally {
            this.addingAllSupplierProducts = false;
            this.cdr.markForCheck();
        }
    }

    private formatStickyPrice(cents: number): string {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format((Number(cents) || 0) / 100);
    }

    getBreadcrumbName(name: string | undefined): string {
        if (!name) return '';
        return name === '__root_collection__' ? 'Home' : name;
    }

    get catalogPageTitle(): string {
        if (this.isConsolidatedFeed) {
            return '';
        }
        const searchTerm = this.consolidatedFeedSearchTerm?.trim();
        if (this.isSearchResultsPage && searchTerm) {
            const count = this.latestTotalResults || this.unfilteredTotalItems || 0;
            return `${count} ${count === 1 ? 'result' : 'results'} for "${searchTerm}"`;
        }
        if (this.isSearchResultsPage) {
            return 'Search results';
        }
        if (this.brandDisplayName?.trim()) {
            return this.brandDisplayName.trim();
        }
        const collectionName = this.latestCollection?.name?.trim();
        if (collectionName && collectionName.toLowerCase() !== 'products') {
            return collectionName.replace(/\s+catalog$/i, '').trim() || collectionName;
        }
        return 'Catalog';
    }

    get isSearchResultsPage(): boolean {
        return this.router.url.split('?')[0] === '/search';
    }

    get catalogOfferCount(): number | null {
        return this.unfilteredTotalItems > 0 ? this.unfilteredTotalItems : null;
    }

    get catalogBrandCount(): number | null {
        return this.countFacetValuesByFacetName(['brand', 'brands']);
    }

    get catalogSellerCount(): number | null {
        return this.countFacetValuesByFacetName([
            'seller',
            'sellers',
            'supplier',
            'suppliers',
        ]);
    }

    toggleBulkBuyPanel(): void {
        this.bulkBuyOpen = !this.bulkBuyOpen;
    }

    onBulkBuyPasteInput(event: Event): void {
        const target = event.target as HTMLTextAreaElement | null;
        this.bulkBuyPasteText = target?.value ?? "";
    }

    async submitBulkBuyPaste(): Promise<void> {
        const codes = extractEanCodesFromText(this.bulkBuyPasteText);
        if (!codes.length) {
            this.notificationService
                .error("Paste at least one valid GTIN / EAN code.")
                .subscribe();
            return;
        }
        if (this.bulkBuyPasteSubmitting) {
            return;
        }
        this.bulkBuyPasteSubmitting = true;
        this.cdr.markForCheck();
        try {
            const result = await this.cartUploadService.addCheapestVariantsToCart(
                codes.map((gtin) => ({ gtin, quantity: 1 })),
            );
            this.failedProductGtins = result.failedProductGtins;
            this.moqViolations = result.moqViolations || [];
            this.bulkBuyPasteText = "";
            this.notificationService
                .success(
                    `Added ${result.successfulCount} product${
                        result.successfulCount === 1 ? "" : "s"
                    } to cart.`,
                )
                .subscribe();
        } catch (error: any) {
            this.notificationService
                .error(error?.message || "Failed to build cart from pasted codes.")
                .subscribe();
        } finally {
            this.bulkBuyPasteSubmitting = false;
            this.cdr.markForCheck();
        }
    }

    toggleMobileMenu() {
        this.mobileMenuOpen = !this.mobileMenuOpen;
    }

    private countFacetValuesByFacetName(names: string[]): number | null {
        if (!this.facetValues?.length) {
            return null;
        }
        const wanted = names.map((name) => name.toLowerCase());
        const ids = new Set<string>();
        for (const entry of this.facetValues) {
            const facetName = (entry.facetValue?.facet?.name || '').toLowerCase();
            if (!wanted.some((name) => facetName === name || facetName.includes(name))) {
                continue;
            }
            ids.add(String(entry.facetValue.id));
        }
        return ids.size > 0 ? ids.size : null;
    }
    sortDropdownOpen = false;
    activeSortLabel = 'Best price';
    expandedProductIds = new Set<string>();
    activeSupplyTypes: string[] = [];
    readonly isLoggedIn$: Observable<boolean>;

    toggleOffers(productId: string) {
        if (this.expandedProductIds.has(productId)) {
            this.expandedProductIds.delete(productId);
        } else {
            this.expandedProductIds.add(productId);
        }
    }

    getOtherOffers(product: EnhancedSearchItem) {
        if (this.supplierId) {
            return [];
        }
        let variants = filterVariantsWithValidSupplier(product.variants || []).filter(
            (v: ProductListVariant) => v.id !== product.cheapestVariant?.id,
        );
        if (this.activeSupplyTypes.length > 0) {
            variants = filterVariantsBySupplyTypes(variants, this.activeSupplyTypes);
        }
        return variants;
    }

    getTotalQty(product: EnhancedSearchItem) {
        let variants = product.variants || [];
        if (this.activeSupplyTypes.length > 0) {
            variants = filterVariantsBySupplyTypes(variants, this.activeSupplyTypes);
        }
        return variants
            .filter((v: ProductListVariant) => v.supplyType !== "pre-order")
            .reduce((sum: number, v: ProductListVariant) => sum + (v.qty || 0), 0);
    }

    get showSupplyTypeFilter(): boolean {
        if (this.isConsolidatedFeed) {
            return false;
        }
        return supportsSupplyTypeFilter(this.collectionSlug, this.supplierId);
    }

    isValidSupplierId = isValidSupplierId;

    getMoq = getMoq;
    getMinQ = getMinQ;
    getMaxQ = getMaxQ;
    getQuantityInfoText = getQuantityInfoText;
    hasMinQ = hasMinQ;
    getBundleLabel = getBundleLabel;

    getSupplierFullPackageOnly(): boolean {
        return !!this.supplierInfo?.fullPackageOnly;
    }

    private getProductContext(
        product?: PackageProductContext | null,
    ): PackageProductContext | null {
        return product ?? null;
    }

    getQuantity(productId: string, variant: any, product?: PackageProductContext): number {
        if (!variant) return 1;
        const key = variant.id ? `${productId}_${variant.id}` : productId;
        if (this.itemQuantities[key] === undefined) {
            this.itemQuantities[key] = getInitialQuantity(
                variant,
                this.getProductContext(product),
                this.getSupplierFullPackageOnly(),
            );
        }
        return this.itemQuantities[key];
    }

    increaseQuantity(productId: string, variant: any, product?: PackageProductContext) {
        if (!variant) return;
        const key = variant.id ? `${productId}_${variant.id}` : productId;
        const productContext = this.getProductContext(product);
        const currentQty = this.getQuantity(productId, variant, product);
        const maxQ = this.getMaxQ(variant);
        const step = getOrderStep(
            variant,
            productContext,
            this.getSupplierFullPackageOnly(),
        );
        const newQty = currentQty + step;

        if (maxQ !== null && maxQ > 0 && newQty > maxQ) {
            this.notificationService
                .error(`Maximum order quantity is ${maxQ}`)
                .subscribe();
            return;
        }

        this.itemQuantities[key] = newQty;
    }

    decreaseQuantity(productId: string, variant: any, product?: PackageProductContext) {
        if (!variant) return;
        const key = variant.id ? `${productId}_${variant.id}` : productId;
        const productContext = this.getProductContext(product);
        const currentQty = this.getQuantity(productId, variant, product);
        const minQ = this.getMinQ(variant);
        const step = getOrderStep(
            variant,
            productContext,
            this.getSupplierFullPackageOnly(),
        );
        const newQty = currentQty - step;

        if (newQty >= minQ) {
            this.itemQuantities[key] = newQty;
        } else {
            this.notificationService
                .error(`Minimal order quantity is ${minQ}`)
                .subscribe();
        }
    }

    onQuantityChange(
        productId: string,
        variant: any,
        event: Event,
        product?: PackageProductContext,
    ) {
        if (!variant) return;
        const input = event.target as HTMLInputElement;
        const key = variant.id ? `${productId}_${variant.id}` : productId;
        const productContext = this.getProductContext(product);
        const minQ = this.getMinQ(variant);
        const step = getOrderStep(
            variant,
            productContext,
            this.getSupplierFullPackageOnly(),
        );

        let value = parseInt(input.value, 10);
        if (isNaN(value) || value < minQ) {
            value = minQ;
            this.notificationService
                .error(`Minimal order quantity is ${minQ}`)
                .subscribe();
        } else {
            const normalized = normalizePackageQuantity(
                value,
                variant,
                productContext,
                this.getSupplierFullPackageOnly(),
            );
            if (normalized !== value) {
                this.notificationService
                    .error(`Quantity rounded to full package step of ${step}: ${normalized}`)
                    .subscribe();
            }
            value = normalized;
        }

        this.itemQuantities[key] = value;
        input.value = value.toString();
    }

    sortOptions = [
        { label: "Best price", field: "price", order: "ASC" },
        { label: "Highest price", field: "price", order: "DESC" },
        { label: "Name (A-Z)", field: "name", order: "ASC" },
        { label: "Name (Z-A)", field: "name", order: "DESC" },
    ];

    supplierSortOptions = [
        { label: "Best vs Retail", field: "price", order: "ASC" },
        { label: "Best price", field: "price", order: "ASC" },
        { label: "Highest price", field: "price", order: "DESC" },
        { label: "Name (A-Z)", field: "name", order: "ASC" },
        { label: "Name (Z-A)", field: "name", order: "DESC" },
    ];

    zoomedImage: string | null = null;

    activeFilters$: Observable<ActiveFilter[]>;

    facetPageSize = 20;
    facetSkip = 0;
    facetTotalItems = 0;
    facetFilterTerm: string | null = null;
    filtersLoading$: Observable<boolean>;
    private facetLoadStatus$ = new BehaviorSubject<boolean>(true);
    private facetValuesLoaded$ = new BehaviorSubject<boolean>(false);
    private readonly destroy$ = new Subject<void>();

    constructor(
        private dataService: DataService,
        private route: ActivatedRoute,
        private stateService: StateService,
        private sanitizer: DomSanitizer,
        private notificationService: NotificationService,
        private exportDownloadService: ExportDownloadService,
        private modalService: ModalService,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private productListViewCache: ProductListViewCacheService,
        private consolidatedFeedStorefront: ConsolidatedFeedStorefrontService,
        private cartUploadService: CartUploadService,
    ) {
        this.isConsolidatedFeed = !!this.route.snapshot.data["consolidatedFeed"];
        if (!this.isConsolidatedFeed) {
            this.consolidatedFeedPricingIds$.next(null);
        }
        this.activeCustomer$ = this.dataService
            .query<GetActiveCustomerQuery>(GET_ACTIVE_CUSTOMER)
            .pipe(
                map((data) => data.activeCustomer),
                catchError(() =>
                    of(undefined as GetActiveCustomerQuery["activeCustomer"])
                ),
                tap((customer) => {
                    if (customer) {
                        this.stateService.setState("activeCustomer", customer);
                    }
                }),
                shareReplay(1)
            );

        this.isLoggedIn$ = this.activeCustomer$.pipe(map(c => !!c));

        this.cartUpdate
            .pipe(
                switchMap(() =>
                    this.dataService.query<GetActiveOrderResult>(
                        GET_ACTIVE_ORDER
                    )
                ),
                takeUntil(this.destroy$)
            )
            .subscribe(
                (result) => {
                    if (result && result.activeOrder) {
                        this.activeOrder = result.activeOrder;
                        if (this.supplierInfo) {
                            this.calculateSupplierSpecificTotal();
                        }
                        this.stateService.setState(
                            "activeOrderId",
                            result.activeOrder.id
                        );
                        this.cdr.markForCheck();
                    }
                },
                (error) => {
                    console.error("Error fetching active order:", error);
                    this.activeOrder = null;
                    this.supplierSpecificTotal = 0;
                    this.filteredOrderLines = [];
                }
            );
    }

    openFilterModal() {
        this.filterModalVisibility = !this.filterModalVisibility;
    }

    toggleSidebar() {
        this.showSidebar = !this.showSidebar;
        if (typeof window !== "undefined") {
            localStorage.setItem(PRODUCT_LIST_SIDEBAR_STORAGE_KEY, String(this.showSidebar));
        }
    }

    toggleViewMode(mode: 'grid' | 'table') {
        this.viewMode = mode;
    }

    toggleSortDropdown() {
        this.sortDropdownOpen = !this.sortDropdownOpen;
    }

    selectSortOption(option: SortOption) {
        this.sortField = option.field;
        this.sortOrder = option.order;
        this.activeSortLabel = option.label;
        this.sortDropdownOpen = false;
        
        this.router.navigate(["./"], {
            queryParams: {
                [SORT_QUERY_PARAM]: option.field,
                [ORDER_QUERY_PARAM]: option.order,
            },
            queryParamsHandling: "merge",
            relativeTo: this.route,
        });
        
        this.refresh.next();
    }

    onSort(field: string) {
        if (this.sortField === field) {
            this.sortOrder = this.sortOrder === "ASC" ? "DESC" : "ASC";
        } else {
            this.sortField = field;
            this.sortOrder = "ASC";
        }
        
        const option = this.sortOptions.find(o => o.field === field && o.order === this.sortOrder) 
                      || this.sortOptions.find(o => o.field === field);
        if (option) {
            this.activeSortLabel = option.label;
        }

        this.refresh.next();
    }

    toggleShowAllOffers(product: EnhancedSearchItem): void {
        product.showAllOffers = !product.showAllOffers;
    }



    get consolidatedFeedCheckoutMovCents(): number {
        return (
            this.consolidatedFeedCustomerMovCents ??
            CONSOLIDATED_FEED_DEFAULT_CHECKOUT_MOV_CENTS
        );
    }

    ngOnInit() {
        this.checkViewport();
        if (this.isConsolidatedFeed) {
            this.loadStorefrontConsolidatedFeed();
            const supplyParam = this.route.snapshot.queryParamMap.get(
                SUPPLY_TYPE_FILTER_QUERY_PARAM,
            );
            if (supplyParam) {
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { [SUPPLY_TYPE_FILTER_QUERY_PARAM]: null },
                    queryParamsHandling: "merge",
                    replaceUrl: true,
                });
            }
        }
        if (typeof window !== "undefined") {
            const saved = localStorage.getItem(PRODUCT_LIST_SIDEBAR_STORAGE_KEY);
            this.showSidebar = saved !== "false";
        }
        this.perPage$ = this.route.queryParamMap.pipe(
            map((pm) => {
                const perPageParam = pm.get(PER_PAGE_QUERY_PARAM);
                const value = parseInt(perPageParam || "25", 10);
                return [5, 10, 25, 50, 100].includes(value) ? value : 50;
            }),
            distinctUntilChanged()
        );

        this.currentPage$ = this.route.queryParamMap.pipe(
            map((pm) => {
                const pageParam = pm.get(PAGE_QUERY_PARAM);
                return parseInt(pageParam || "1", 10);
            }),
            distinctUntilChanged(),
            tap((page) => {
                this.stateService.setState("lastPageNumber", page);
            })
        );

        const supplierId$ = this.route.paramMap.pipe(
            map((pm) => pm.get("supplierId")),
            distinctUntilChanged(),
            tap((supplierId) => {
                this.supplierId = supplierId;
                this.selectedSupplierTierMinCents = null;
                this.selectedSupplierProductIds = new Set<string>();
                this.latestSupplierPageProducts = [];
                if (supplierId) {
                    this.fetchSupplierInfo(supplierId);
                    this.viewMode = 'table';
                    this.activeSortLabel = 'Best vs Retail';
                } else {
                    this.supplierInfo = null;
                }
            }),
            shareReplay(1)
        );

        supplierId$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.fetchActiveOrder();
        });

        const brandSlug$ = this.route.paramMap.pipe(
            map((pm) => pm.get("brandSlug")),
            distinctUntilChanged(),
            tap((brandSlug) => {
                this.brandSlug = brandSlug;
            }),
            shareReplay(1)
        );

        const brandFacet$: Observable<{ id: string; name: string } | null> =
            brandSlug$.pipe(
                switchMap((brandSlug): Observable<{ id: string; name: string } | null> => {
                    if (!brandSlug) {
                        this.brandDisplayName = null;
                        this.activeFacetValueIdsFromBrand = null;
                        return of(null);
                    }
                    return this.resolveBrandFacet(brandSlug).pipe(
                        tap((brand) => {
                            this.brandDisplayName = brand?.name ?? null;
                            this.activeFacetValueIdsFromBrand = brand?.id ?? null;
                        }),
                    );
                }),
                shareReplay(1)
            );

        const consolidated$ = this.route.data.pipe(
            map((data) => !!data["consolidatedFeed"]),
            tap((value) => {
                this.isConsolidatedFeed = value;
            }),
            distinctUntilChanged(),
            shareReplay(1),
        );

        const collectionSlug$ = combineLatest([
            this.route.paramMap.pipe(map((pm) => pm.get("slug"))),
            brandSlug$,
            consolidated$,
        ]).pipe(
            map(([slug, brandSlug, consolidated]) => {
                if (consolidated) {
                    return PRODUCTS_COLLECTION_SLUG;
                }
                return slug || (brandSlug ? PRODUCTS_COLLECTION_SLUG : null);
            }),
            distinctUntilChanged(),
            tap((slug) => {
                this.collectionSlug = slug;
                this.stateService.setState("lastCollectionSlug", slug || null);
            }),
            shareReplay(1)
        );

        this.activeFacetValueIds$ = combineLatest([
            this.route.paramMap.pipe(
                map((pm) => getRouteArrayParam(pm, "facets")),
            ),
            brandFacet$,
        ]).pipe(
            map(([facetIds, brand]) => {
                if (!brand?.id) {
                    return facetIds;
                }
                return facetIds.includes(brand.id)
                    ? facetIds
                    : [...facetIds, brand.id];
            }),
            distinctUntilChanged((x, y) => x.toString() === y.toString()),
            shareReplay(1)
        );

        this.searchTerm$ = this.route.queryParamMap.pipe(
            map((pm) => readSearchTermParam(pm)),
            distinctUntilChanged(),
            shareReplay(1)
        );

        this.searchTerm$
            .pipe(takeUntil(this.destroy$))
            .subscribe((term) => {
                this.consolidatedFeedSearchTerm = term;
                this.supplierPricelistSearchTerm = term;
                if (this.isSearchResultsPage) {
                    this.viewMode = 'grid';
                }
                this.cdr.markForCheck();
            });

        const shippingDateRanges$ = this.route.queryParamMap.pipe(
            map((pm) => {
                const shippingDate = pm.get(SHIPPING_DATE_FILTER_QUERY_PARAM);
                return shippingDate ? shippingDate.split(",") : [];
            }),
            distinctUntilChanged((x, y) => x.toString() === y.toString()),
            shareReplay(1)
        );

        const movRanges$ = this.route.queryParamMap.pipe(
            map((pm) => {
                const mov = pm.get(MOV_FILTER_QUERY_PARAM);
                return mov ? mov.split(",") : [];
            }),
            distinctUntilChanged((x, y) => x.toString() === y.toString()),
            shareReplay(1)
        );

        const supplyTypes$ = this.route.queryParamMap.pipe(
            map((pm) => {
                const param = pm.get(SUPPLY_TYPE_FILTER_QUERY_PARAM);
                return param
                    ? param.split(",").map((v) => v.trim()).filter(Boolean)
                    : [];
            }),
            distinctUntilChanged((x, y) => x.join(",") === y.join(",")),
            shareReplay(1)
        );

        const effectiveSupplyTypes$ = combineLatest([
            supplyTypes$,
            collectionSlug$,
            supplierId$,
        ]).pipe(
            map(([types, slug, supplierId]) => {
                return supportsSupplyTypeFilter(slug, supplierId) ? types : [];
            }),
            distinctUntilChanged((x: string[], y: string[]) => x.join(",") === y.join(",")),
            shareReplay(1),
        );

        effectiveSupplyTypes$.pipe(takeUntil(this.destroy$)).subscribe((types) => {
            this.activeSupplyTypes = types;
        });

        this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((pm) => {
            const sort = pm.get(SORT_QUERY_PARAM);
            const order = pm.get(ORDER_QUERY_PARAM) as "ASC" | "DESC" | null;
            
            if (sort) {
                this.sortField = sort;
                this.sortOrder = order || 'ASC';
                const option = this.sortOptions.find(o => o.field === sort && o.order === (order || 'ASC'));
                if (option) {
                    this.activeSortLabel = option.label;
                }
            } else if (this.supplierId) {
                this.sortField = 'price';
                this.sortOrder = 'ASC';
                this.activeSortLabel = 'Best price';
            }
        });

        this.collection$ = collectionSlug$.pipe(
            switchMap((slug) => {
                if (slug) {
                    return this.dataService
                        .query<GetCollectionQuery, GetCollectionQueryVariables>(
                            GET_COLLECTION,
                            {
                                slug,
                            }
                        )
                        .pipe(map((data) => data.collection));
                } else {
                    return of(undefined);
                }
            }),
            tap((collection) => {
                this.latestCollection = collection ?? undefined;
            }),
            shareReplay(1)
        );

        const assetPreviewPipe = new AssetPreviewPipe();

        this.mastheadBackground$ = this.collection$.pipe(
            map(
                (c) =>
                    "url(" +
                    assetPreviewPipe.transform(
                        c?.featuredAsset || undefined,
                        1000,
                        300
                    ) +
                    ")"
            ),
            map((style) => this.sanitizer.bypassSecurityTrustStyle(style))
        );

        this.breadcrumbs$ = combineLatest([this.collection$, brandFacet$, consolidated$]).pipe(
            map(([collection, brand, consolidated]) => {
                if (consolidated) {
                    return [
                        { id: "", name: "__root_collection__", slug: "" },
                        { id: "", name: "Consolidated Feed", slug: "consolidated-feed" },
                    ];
                }
                if (brand) {
                    return [
                        { id: "", name: "__root_collection__", slug: "" },
                        { id: brand.id, name: brand.name, slug: "" },
                    ];
                }
                if (collection) {
                    return collection.breadcrumbs;
                }
                return [
                    {
                        id: "",
                        name: "__root_collection__",
                        slug: "",
                    },
                    {
                        id: "",
                        name: this.isSearchResultsPage ? "Search results" : "Health & Beauty Catalog",
                        slug: "search",
                    },
                ];
            })
        );

        const triggerFetch$ = combineLatest([
            this.collection$,
            this.activeFacetValueIds$,
            this.searchTerm$,
            this.refresh,
        ]).pipe(
            map(([collection, facetValueIds, term]) => ({
                collection,
                facetValueIds,
                term,
                sortField: this.sortField,
                sortOrder: this.sortOrder
            }))
        );

        const applyFacetPage = (
            items: SearchProductsQuery["search"]["facetValues"],
            totalItems: number,
            append: boolean,
            cacheKey?: string,
        ) => {
            const nextItems = (
                append
                    ? [...(this.facetValues || []), ...items]
                    : items
            ).filter(Boolean);
            this.facetValues = nextItems;
            this.facetTotalItems = totalItems;
            this.facetSkip = nextItems.length;
            this.facetLoadStatus$.next(false);
            this.facetValuesLoaded$.next(nextItems.length > 0);
            if (cacheKey) {
                this.productListViewCache.setFacets(cacheKey, {
                    items: nextItems,
                    totalItems,
                });
            }
        };

        const loadFacetPage = (reset = false) => {
            combineLatest([this.collection$, this.searchTerm$])
                .pipe(
                    take(1),
                    switchMap(([collection, term]) => {
                        const cacheKey = this.productListViewCache.facetKey(
                            collection?.id ?? null,
                            term,
                            this.supplierId,
                        );
                        if (reset) {
                            this.facetSkip = 0;
                            const cached =
                                this.productListViewCache.getFacets(cacheKey);
                            if (cached) {
                                this.facetValues = cached.items;
                                this.facetTotalItems = cached.totalItems;
                                this.facetSkip = cached.items.length;
                                this.facetValuesLoaded$.next(
                                    cached.items.length > 0,
                                );
                                this.facetLoadStatus$.next(false);
                            } else {
                                this.facetLoadStatus$.next(true);
                                if (!this.facetValues?.length) {
                                    this.facetValuesLoaded$.next(false);
                                }
                            }
                        }
                        return this.dataService
                            .query<
                                SearchFacetValuesQuery,
                                SearchFacetValuesQueryVariables
                            >(
                                SEARCH_FACET_VALUES,
                                {
                                    input: {
                                        term,
                                        groupByProduct: true,
                                        collectionId: collection?.id,
                                        ...(this.supplierSearchScope()),
                                    },
                                    take: this.facetPageSize,
                                    skip: reset ? 0 : this.facetSkip,
                                    filterTerm: this.facetFilterTerm,
                                },
                                "cache-first",
                            )
                            .pipe(
                                map((data) => ({
                                    data,
                                    cacheKey,
                                    reset,
                                })),
                            );
                    }),
                    takeUntil(this.destroy$)
                )
                .subscribe({
                    next: ({ data, cacheKey, reset }) => {
                        const page = data.searchFacetValues;
                        const items =
                            page.items as SearchProductsQuery["search"]["facetValues"];
                        applyFacetPage(
                            items,
                            page.totalItems,
                            !reset,
                            cacheKey,
                        );
                    },
                    error: (error) => {
                        console.error("Error loading facets:", error);
                        this.facetLoadStatus$.next(false);
                        this.facetValuesLoaded$.next(
                            !!(this.facetValues && this.facetValues.length > 0),
                        );
                    },
                });
        };

        const consolidatedScope$ = this.isConsolidatedFeed
            ? this.consolidatedFeedPricingIds$.pipe(
                  filter((ids): ids is number[] => ids !== null),
              )
            : of(undefined as number[] | undefined);

        const searchParams$ = combineLatest([
            triggerFetch$,
            supplierId$,
            this.currentPage$,
            this.perPage$,
            shippingDateRanges$,
            movRanges$,
            effectiveSupplyTypes$,
            consolidatedScope$,
        ]).pipe(
            map((result) => {
                const trigger = result[0] as {
                    collection: GetCollectionQuery["collection"];
                    facetValueIds: string[];
                    term: string;
                    sortField?: string | null;
                    sortOrder?: "ASC" | "DESC" | null;
                };
                const supplierId = result[1] as string | null;
                const currentPage = result[2] as number;
                const perPage = result[3] as number;
                const shippingRanges = result[4] as string[];
                const movRanges = result[5] as string[];
                const supplyTypes = result[6] as string[];
                const supplierIds = result[7] as number[] | undefined;

                const { collection, facetValueIds, term, sortField, sortOrder } =
                    trigger;

                const facetValueFilters =
                    facetValueIds.length > 0 ? [{ or: facetValueIds }] : [];

                const searchInput = getSearchInput(
                    term,
                    collection,
                    facetValueFilters,
                    this.isConsolidatedFeed ? null : supplierId,
                    perPage,
                    currentPage,
                    shippingRanges || undefined,
                    movRanges || undefined,
                    sortField,
                    sortOrder,
                    supplyTypes.length > 0 ? supplyTypes : undefined,
                    supplierIds,
                );

                return { searchInput, facetValueIds };
            }),
            shareReplay(1),
        );

        const normalizeSearchItems = (
            rawItems: SearchItem[],
        ): EnhancedSearchItem[] =>
            rawItems.map((item) => {
                const typedItem = item as EnhancedSearchItem;
                const vs = filterVariantsWithValidSupplier(
                    typedItem.variants || [],
                );
                const cheapest = pickCheapestOffer(vs, typedItem.cheapestVariant);
                return {
                    ...item,
                    variants: vs,
                    cheapestVariant: cheapest,
                };
            });

        const countSearchParams$ = combineLatest([
            triggerFetch$,
            supplierId$,
            shippingDateRanges$,
            movRanges$,
            effectiveSupplyTypes$,
            consolidatedScope$,
        ]).pipe(
            map((result) => {
                const trigger = result[0] as {
                    collection: GetCollectionQuery["collection"];
                    facetValueIds: string[];
                    term: string;
                    sortField?: string | null;
                    sortOrder?: "ASC" | "DESC" | null;
                };
                const supplierId = result[1] as string | null;
                const shippingRanges = result[2] as string[];
                const movRanges = result[3] as string[];
                const supplyTypes = result[4] as string[];
                const supplierIds = result[5] as number[] | undefined;

                const { collection, facetValueIds, term, sortField, sortOrder } =
                    trigger;

                const facetValueFilters =
                    facetValueIds.length > 0 ? [{ or: facetValueIds }] : [];

                const searchInput = getSearchInput(
                    term,
                    collection,
                    facetValueFilters,
                    this.isConsolidatedFeed ? null : supplierId,
                    25,
                    1,
                    shippingRanges || undefined,
                    movRanges || undefined,
                    sortField,
                    sortOrder,
                    supplyTypes.length > 0 ? supplyTypes : undefined,
                    supplierIds,
                );

                return { searchInput, facetValueIds, term };
            }),
            shareReplay(1),
        );

        const searchFetchPolicy = (term: string) =>
            term.trim().length > 0 ? "network-only" : "cache-and-network";

        const productsResult$ = searchParams$.pipe(
            switchMap(({ searchInput }) => {
                const term = searchInput.input.term?.trim() ?? "";
                return this.dataService
                    .query<SearchProductsQuery, SearchProductsQueryVariables>(
                        SEARCH_PRODUCTS,
                        {
                            input: {
                                ...searchInput.input,
                                skipTotalCount: true,
                            },
                        },
                        searchFetchPolicy(term),
                    )
                    .pipe(
                        map((data) => normalizeSearchItems(data?.search?.items ?? [])),
                        tap((items) => {
                            if (searchInput.input.term?.trim() && items.length === 0) {
                                this.unfilteredTotalItems = 0;
                            }
                        }),
                        catchError((err) => {
                            console.error("SearchProducts query failed", err);
                            return of([] as EnhancedSearchItem[]);
                        }),
                    );
            }),
            shareReplay(1),
        );

        const countResult$ = countSearchParams$.pipe(
            switchMap(({ searchInput, facetValueIds, term }) =>
                this.dataService
                    .query<
                        SearchTotalCountQuery,
                        SearchTotalCountQueryVariables
                    >(SEARCH_TOTAL_COUNT, searchInput, searchFetchPolicy(term))
                    .pipe(
                        map((data) => data?.searchTotalCount ?? 0),
                        catchError((err) => {
                            console.error("SearchTotalCount query failed", err);
                            return of(0);
                        }),
                        tap((total) => {
                            if (facetValueIds.length === 0) {
                                this.unfilteredTotalItems = total;
                            }
                        }),
                    ),
            ),
            shareReplay(1),
        );

        this.hasLoadedData$ = productsResult$.pipe(
            mapTo(true),
            startWith(false),
            shareReplay(1),
        );

        this.loading$ = merge(
            searchParams$.pipe(mapTo(true)),
            productsResult$.pipe(mapTo(false)),
        ).pipe(startWith(true));

        const facetLoadTrigger$ = combineLatest([
            this.collection$,
            this.searchTerm$,
            supplierId$,
        ]).pipe(
            map(([collection, term, supplierId]) => ({
                collectionId: collection?.id ?? null,
                term,
                supplierId,
            })),
            distinctUntilChanged(
                (prev, curr) =>
                    prev.collectionId === curr.collectionId &&
                    prev.term === curr.term &&
                    prev.supplierId === curr.supplierId
            )
        );

        facetLoadTrigger$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                loadFacetPage(true);
            });

        this.filtersLoading$ = combineLatest([
            this.facetLoadStatus$,
            this.facetValuesLoaded$,
        ]).pipe(
            map(([isLoading, hasLoaded]) => isLoading && !hasLoaded),
            shareReplay(1)
        );

        const items$ = productsResult$;

        this.products$ = items$.pipe(
            map((items: EnhancedSearchItem[]) => {
                if (!this.supplierId) {
                    return items;
                }
                const currentSupplierId = parseInt(this.supplierId, 10);
                const variantMatchesSupplier = (v: ProductListVariant) =>
                    Number(v?.supplierId) === currentSupplierId;
                return items.map((item: EnhancedSearchItem) => {
                    const supplierOffers = (item.variants || []).filter(
                        variantMatchesSupplier
                    );
                    if (supplierOffers.length === 0) {
                        return item;
                    }
                    const cheapest = pickCheapestOffer(
                        supplierOffers,
                        item.cheapestVariant
                    );
                    return {
                        ...item,
                        cheapestVariant: cheapest,
                    };
                });
            })
        );

        this.totalResults$ = countResult$.pipe(
            distinctUntilChanged(),
            tap((total) => {
                this.latestTotalResults = total;
            }),
            shareReplay(1),
        );

        this.totalPages$ = combineLatest([
            this.totalResults$,
            this.perPage$,
        ]).pipe(
            map(([total, perPage]) => Math.ceil(total / perPage) || 1),
            distinctUntilChanged()
        );

        this.canGoPrevious$ = this.currentPage$.pipe(
            map((currentPage) => currentPage > 1)
        );

        this.canGoNext$ = combineLatest([
            this.currentPage$,
            this.totalPages$,
        ]).pipe(map(([currentPage, totalPages]) => currentPage < totalPages));

        this.activeFilters$ = combineLatest([
            this.activeFacetValueIds$,
            shippingDateRanges$,
            movRanges$,
            effectiveSupplyTypes$,
            this.collection$
        ]).pipe(
            map(([facetIds, shipping, mov, supplyTypes, _collection]) => {
                const filters: ActiveFilter[] = [];
                
                facetIds.forEach(id => {
                    const facetValue = this.facetValues.find(fv => fv.facetValue.id === id);
                    if (facetValue) {
                        filters.push({ 
                            label: facetValue.facetValue.facet.name, 
                            value: facetValue.facetValue.name, 
                            type: 'facet', 
                            id 
                        });
                    } else {
                        filters.push({ label: 'Filter', value: id, type: 'facet', id });
                    }
                });
                
                shipping.forEach(s => {
                    filters.push({ label: 'Ships by', value: s, type: 'shipping' });
                });
                
                mov.forEach(m => {
                    filters.push({ label: 'MOV', value: m, type: 'mov' });
                });

                supplyTypes.forEach((type) => {
                    const label =
                        type === SUPPLY_TYPE.STOCK
                            ? "In Stock"
                            : type === SUPPLY_TYPE.PRE_ORDER
                              ? "Pre-Order"
                              : type;
                    filters.push({
                        label: "Supply Type",
                        value: label,
                        type: "supplyType",
                    });
                });
                
                return filters;
            })
        );

        this.products$
            .pipe(takeUntil(this.destroy$))
            .subscribe((products) => {
                products.forEach((product) => {
                    const variant = product.cheapestVariant;
                    if (variant) {
                        const key = variant.id ? `${product.productId}_${variant.id}` : product.productId;
                        if (this.itemQuantities[key] === undefined) {
                            this.itemQuantities[key] = getInitialQuantity(
                                variant,
                                product,
                                this.getSupplierFullPackageOnly(),
                            );
                        }
                    }
                });
            });

        this.products$
            .pipe(
                switchMap((products) => {
                    const productIds = products
                        .map((product) => product.productId)
                        .filter(Boolean);
                    if (productIds.length === 0) {
                        this.retailPriceRanges = {};
                        return of([]);
                    }
                    return this.dataService
                        .query<
                            {
                                productRetailPriceRanges: Array<{
                                    productId: string;
                                    min: number | null;
                                    max: number | null;
                                    currency: string | null;
                                }>;
                            },
                            { productIds: string[] }
                        >(GET_PRODUCT_RETAIL_PRICE_RANGES, { productIds })
                        .pipe(
                            map((result) => result.productRetailPriceRanges ?? []),
                            catchError(() => of([])),
                        );
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((ranges) => {
                const next: typeof this.retailPriceRanges = {};
                for (const range of ranges) {
                    const min = range.min != null ? Number(range.min) : null;
                    const max = range.max != null ? Number(range.max) : null;
                    next[String(range.productId)] = {
                        min: min != null && !isNaN(min) ? min : null,
                        max: max != null && !isNaN(max) ? max : null,
                        currency: range.currency,
                    };
                }
                this.retailPriceRanges = next;
                this.cdr.markForCheck();
            });
    }

    formatRetailPriceRange(productId: string): string | null {
        const range = this.retailPriceRanges[String(productId)];
        if (!range || range.min == null || range.max == null || !range.currency) {
            return null;
        }
        const min = Number(range.min);
        const max = Number(range.max);
        if (isNaN(min) || isNaN(max)) {
            return null;
        }
        const fmt = (v: number) =>
            new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: range.currency!,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(v / 100);
        if (min === max) {
            return fmt(min);
        }
        return `${fmt(min)} – ${fmt(max)}`;
    }

    hasRetailPriceRange(productId: string): boolean {
        const range = this.retailPriceRanges[String(productId)];
        return range?.min != null && range?.max != null;
    }

    getCatalogOfferCount(product: EnhancedSearchItem): number {
        const variants = filterVariantsWithValidSupplier(product?.variants || []);
        return Math.max(variants.length, product?.cheapestVariant ? 1 : 0);
    }

    getCatalogMarginPercent(product: EnhancedSearchItem): number | null {
        const wholesale = product?.cheapestVariant?.priceWithTax;
        const range = this.retailPriceRanges[String(product?.productId)];
        if (
            wholesale == null ||
            range?.min == null ||
            range?.max == null ||
            Number(range.max) <= 0
        ) {
            return null;
        }
        const retail = (Number(range.min) + Number(range.max)) / 2;
        const pct = Math.round(((retail - Number(wholesale)) / retail) * 100);
        if (!Number.isFinite(pct) || pct <= 0) {
            return null;
        }
        return pct;
    }

    isHotOfferProduct(product: EnhancedSearchItem): boolean {
        const supply = (product?.cheapestVariant?.supplyType || "").toLowerCase();
        return supply === "short-offer" || supply.includes("short");
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadStorefrontConsolidatedFeed(): void {
        this.dataService
            .query<{
                storefrontConsolidatedFeed: {
                    id: string;
                    name: string;
                    customerMov?: number | null;
                    pricingIds: Array<string | number>;
                    liveUrls?: {
                        csv?: string | null;
                        xlsx?: string | null;
                        xml?: string | null;
                    } | null;
                } | null;
            }>(GET_STOREFRONT_CONSOLIDATED_FEED)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => {
                    const feed = data.storefrontConsolidatedFeed;
                    this.consolidatedFeedPricingIds = (feed?.pricingIds ?? [])
                        .map((id) => Number(id))
                        .filter((id) => Number.isFinite(id) && id > 0);
                    this.consolidatedFeedLiveUrls = feed?.liveUrls ?? null;
                    this.consolidatedFeedCustomerMovCents =
                        feed?.customerMov != null && feed.customerMov > 0
                            ? feed.customerMov
                            : null;
                    this.consolidatedFeedStorefront.setCheckoutMovCents(
                        feed?.customerMov,
                    );
                    this.consolidatedFeedLoaded = true;
                    this.consolidatedFeedPricingIds$.next(
                        this.consolidatedFeedPricingIds,
                    );
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.consolidatedFeedLoaded = true;
                    this.consolidatedFeedPricingIds$.next([]);
                    this.cdr.markForCheck();
                },
            });
    }

    private supplierSearchScope(): {
        supplierId?: number;
        supplierIds?: number[];
    } {
        if (this.isConsolidatedFeed) {
            return { supplierIds: this.consolidatedFeedPricingIds };
        }
        if (this.supplierId) {
            return { supplierId: parseInt(this.supplierId, 10) };
        }
        return {};
    }

    private openConsolidatedFeedFile(
        format: ExportFormat | "xml",
    ): boolean {
        const url =
            format === "xlsx"
                ? this.consolidatedFeedLiveUrls?.xlsx
                : format === "xml"
                  ? this.consolidatedFeedLiveUrls?.xml
                  : this.consolidatedFeedLiveUrls?.csv;
        if (!url) {
            return false;
        }
        window.open(url, "_blank");
        return true;
    }

    onFacetSearchTermChange(term: string): void {
        this.facetFilterTerm = term?.trim() || null;
        combineLatest([this.collection$, this.searchTerm$])
            .pipe(
                take(1),
                switchMap(([collection, searchTerm]) =>
                    this.dataService.query<
                        SearchFacetValuesQuery,
                        SearchFacetValuesQueryVariables
                    >(SEARCH_FACET_VALUES, {
                        input: {
                            term: searchTerm,
                            groupByProduct: true,
                            collectionId: collection?.id,
                            ...(this.supplierSearchScope()),
                        },
                        take: this.facetPageSize,
                        skip: 0,
                        filterTerm: this.facetFilterTerm,
                    })
                ),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: (data) => {
                    const page = data.searchFacetValues;
                    this.facetTotalItems = page.totalItems;
                    this.facetValues = page.items || [];
                    this.facetSkip = this.facetPageSize;
                    this.facetLoadStatus$.next(false);
                    if (this.facetValues && this.facetValues.length > 0) {
                        this.facetValuesLoaded$.next(true);
                    } else {
                        this.facetValuesLoaded$.next(false);
                    }
                },
                error: (error) => {
                    console.error("Error loading facets:", error);
                    this.facetLoadStatus$.next(false);
                    this.facetValuesLoaded$.next(false);
                },
            });
    }

    fetchSupplierInfo(supplierId: string): void {
        this.dataService
            .mutate<GetSupplierInfoResult, { supplierId: string }>(
                GET_SUPPLIER_INFO,
                {
                    supplierId,
                }
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (result) => {
                    if (result && result.getSupplierInfo) {
                        this.supplierInfo = result.getSupplierInfo;

                        if (this.activeOrder) {
                            this.calculateSupplierSpecificTotal();
                        }
                        this.checkCsvFileStatus(supplierId);
                    } else {
                        this.supplierInfo = null;
                    }
                    this.cdr.markForCheck();
                },
                (error) => {
                    console.error("Error fetching supplier info:", error);
                    this.supplierInfo = null;
                    this.cdr.markForCheck();
                }
            );
    }

    checkCsvFileStatus(supplierId: string): void {
        let statusUrl;

        if (environment.apiPort) {
            statusUrl = `${environment.apiHost}:${environment.apiPort}/csv/products/supplier/${supplierId}/status`;
        } else {
            statusUrl = `${environment.apiHost}/csv/products/supplier/${supplierId}/status`;
        }

        const token = localStorage.getItem("auth_token");

        fetch(statusUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json();
                }
                throw new Error("Failed to check file status");
            })
            .then((data) => {
                this.csvFileStatus = {
                    exists: data.exists,
                    lastModified: data.lastModified
                        ? new Date(data.lastModified)
                        : null,
                };
            })
            .catch((error) => {
                console.error("Error checking CSV file status:", error);
                this.csvFileStatus = null;
            });
    }

    copyToClipboard(gtin: string, event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        if (!gtin) {
            return;
        }
        navigator.clipboard.writeText(gtin).then(
            () => {
                this.notificationService
                    .success("EAN copied to clipboard")
                    .subscribe();
            },
            (err) => {
                console.error("Could not copy text: ", err);
                this.notificationService
                    .error("Failed to copy EAN")
                    .subscribe();
            }
        );
    }

    fetchActiveOrder(): void {
        this.dataService
            .query<GetActiveOrderResult>(GET_ACTIVE_ORDER)
            .subscribe(
                (result) => {
                    if (result && result.activeOrder) {
                        this.activeOrder = result.activeOrder;

                        if (this.supplierInfo) {
                            this.calculateSupplierSpecificTotal();
                        }
                    } else {
                        this.activeOrder = null;
                        this.supplierSpecificTotal = 0;
                        this.filteredOrderLines = [];
                    }
                },
                (error) => {
                    console.error("Error fetching active order:", error);
                    this.activeOrder = null;
                    this.supplierSpecificTotal = 0;
                    this.filteredOrderLines = [];
                }
            );
    }

    calculateSupplierSpecificTotal(): void {
        this.supplierSpecificTotal = 0;
        this.filteredOrderLines = [];

        if (!this.activeOrder || !this.supplierInfo || !this.supplierInfo.id) {
            return;
        }

        this.filteredOrderLines = this.activeOrder.lines.filter(
            (line) =>
                line.productVariant.pricing &&
                line.productVariant.pricing.id === this.supplierInfo?.id
        );

        this.supplierSpecificTotal = this.filteredOrderLines.reduce(
            (sum, line) =>
                sum +
                ((line as { discountedLinePriceWithTax?: number })
                    .discountedLinePriceWithTax ?? line.linePriceWithTax),
            0
        );
    }

    getSupplierSpecificTotalFormatted(): string {
        return (this.supplierSpecificTotal / 100).toFixed(2);
    }

    isSupplierPreOrder = (): boolean =>
        isSupplierPreOrder(this.supplierInfo);

    readonly pricelistCardText = PRICELIST_CARD_TEXT;
    readonly importText = PRICELIST_TEXT;

    getSupplierDeliveryTerm(): string | null {
        return getSupplierDeliveryTerm(this.supplierInfo);
    }

    getShippingDateDisplay(variant: ProductListVariant | null | undefined): string {
        return DateUtils.getVariantShippingDateDisplay(
            variant,
            this.getSupplierDeliveryTerm()
        );
    }

    showSupplierPrePayment(): boolean {
        return showSupplierPrePayment(this.supplierInfo);
    }

    getRemainingToMinimumOrder(): number {
        return getRemainingToMinimumOrder(this.supplierInfo, this.supplierSpecificTotal);
    }

    getRemainingToMinimumOrderFormatted(): string {
        return (this.getRemainingToMinimumOrder() / 100).toFixed(2);
    }

    isMinimumOrderMet(): boolean {
        return isMinimumOrderMet(this.supplierInfo, this.supplierSpecificTotal);
    }

    getProgressPercentage(): number {
        return getProgressPercentage(this.supplierInfo, this.supplierSpecificTotal);
    }

    getProgressColor(): string {
        return getProgressColor(this.getProgressPercentage());
    }

    getConsolidatedCartTotal(): number {
        if (!this.activeOrder?.lines?.length) {
            return 0;
        }
        const countAllLines = isConsolidatedFeedOrder(this.activeOrder);
        const feedPricingIds = new Set(
            this.consolidatedFeedPricingIds.map((id) => String(id)),
        );
        if (!countAllLines && feedPricingIds.size === 0) {
            return 0;
        }
        return this.activeOrder.lines.reduce((sum, line) => {
            if (!countAllLines) {
                const pricingId = line.productVariant?.pricing?.id;
                if (!pricingId || !feedPricingIds.has(String(pricingId))) {
                    return sum;
                }
            }
            const lineTotal =
                (line as { discountedLinePriceWithTax?: number })
                    .discountedLinePriceWithTax ?? line.linePriceWithTax;
            return sum + lineTotal;
        }, 0);
    }

    showConsolidatedCheckoutNotice(): boolean {
        return this.getConsolidatedCheckoutRemaining() > 0;
    }

    selectConsolidatedMovTier(cents: number): void {
        this.selectedConsolidatedMovTierCents = cents;
    }

    isConsolidatedMovTierSelected(cents: number): boolean {
        return this.selectedConsolidatedMovTierCents === cents;
    }

    formatConsolidatedMovTier(cents: number): string {
        return new Intl.NumberFormat("en-IE", {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 0,
        }).format(cents / 100);
    }

    getConsolidatedRemaining(): number {
        return Math.max(
            0,
            this.selectedConsolidatedMovTierCents - this.getConsolidatedCartTotal(),
        );
    }

    getConsolidatedCheckoutRemaining(): number {
        return Math.max(
            0,
            this.consolidatedFeedCheckoutMovCents - this.getConsolidatedCartTotal(),
        );
    }

    showConsolidatedPreviewNotice(): boolean {
        return (
            this.selectedConsolidatedMovTierCents > CONSOLIDATED_FEED_CHECKOUT_MOV_CENTS
        );
    }

    getConsolidatedProgress(): number {
        if (this.selectedConsolidatedMovTierCents <= 0) {
            return 100;
        }
        return Math.min(
            (this.getConsolidatedCartTotal() / this.selectedConsolidatedMovTierCents) * 100,
            100,
        );
    }

    getConsolidatedBrandCount(): number {
        if (!this.facetValues?.length) {
            return 0;
        }
        return this.facetValues.filter(
            (facetValue) =>
                (facetValue.facetValue?.facet?.name ?? "").toLowerCase() === "brand" ||
                (facetValue.facetValue?.name ?? "").toLowerCase().includes("brand"),
        ).length;
    }

    formatConsolidatedAvailability(
        variant: ProductListVariant | null | undefined,
    ): string {
        if (variant?.supplyType === "pre-order") {
            return "Pre-order";
        }
        const amount = variant?.qty ?? 0;
        if (amount <= 0) {
            return "Out of stock";
        }
        return `${amount.toLocaleString()} units`;
    }

    submitConsolidatedFeedSearch(): void {
        this.setConsolidatedFeedSearchParam(
            this.consolidatedFeedSearchTerm.trim() || null,
        );
    }

    onConsolidatedFeedSearchInput(): void {
        const term = this.consolidatedFeedSearchTerm.trim();
        const activeTerm = readSearchTermParam(
            this.route.snapshot.queryParamMap,
        );
        if (!term && activeTerm) {
            this.setConsolidatedFeedSearchParam(null);
        }
    }

    private setConsolidatedFeedSearchParam(term: string | null): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                [LEGACY_SEARCH_QUERY_PARAM]: term,
                [FACET_SEARCH_ROUTE_QUERY_PARAM]: null,
                [PAGE_QUERY_PARAM]: 1,
            },
            queryParamsHandling: "merge",
        });
    }

    connectFeed(format: ExportFormat | "xml"): void {
        if (this.openConsolidatedFeedFile(format === "xml" ? "xml" : format)) {
            return;
        }
        this.downloadAllProducts(format === "xml" ? "csv" : format);
    }

    downloadConsolidatedFeed(): void {
        this.onCatalogDownload(this.exportDownloadService.getPreferredFormat());
    }

    onCatalogDownload(format: ExportFormat): void {
        const uploadedListTerm = normalizeEanSearchTerm(
            readSearchTermParam(this.route.snapshot.queryParamMap),
        );
        if (
            this.isConsolidatedFeed &&
            !uploadedListTerm &&
            this.openConsolidatedFeedFile(format)
        ) {
            return;
        }
        this.downloadAllProducts(format);
    }

    trackByProductId(item: SearchItem) {
        return item.productId;
    }

    goToPage(page: number) {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { [PAGE_QUERY_PARAM]: page },
            queryParamsHandling: "merge",
        });
    }

    goToFirstPage() {
        this.goToPage(1);
    }

    goToLastPage() {
        combineLatest([this.totalResults$, this.totalPages$])
            .pipe(
                take(1),
                filter(
                    ([totalResults, totalPages]: [number, number]) =>
                        totalResults > 0 && totalPages > 1
                )
            )
            .subscribe(([, totalPages]: [number, number]) => {
                this.goToPage(totalPages);
            });
    }

    changeItemsPerPage(perPage: number) {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                [PAGE_QUERY_PARAM]: 1,
                [PER_PAGE_QUERY_PARAM]: perPage,
            },
            queryParamsHandling: "merge",
        });
        this.perPageDropdownOpen = false;
    }

    togglePerPageDropdown() {
        this.perPageDropdownOpen = !this.perPageDropdownOpen;
    }

    @HostListener("document:click", ["$event"])
    onDocumentClick(event: Event) {
        const target = event.target as HTMLElement;
        if (!target.closest(".per-page-dropdown")) {
            this.perPageDropdownOpen = false;
        }
        if (!target.closest(".product-list__sort-dropdown")) {
            this.sortDropdownOpen = false;
        }
    }

    goToPreviousPage() {
        this.currentPage$.pipe(take(1)).subscribe((currentPage) => {
            this.goToPage(currentPage - 1);
        });
    }

    goToNextPage() {
        this.currentPage$.pipe(take(1)).subscribe((currentPage) => {
            this.goToPage(currentPage + 1);
        });
    }

    getPageNumbers(): Observable<number[]> {
        return combineLatest([this.currentPage$, this.totalPages$]).pipe(
            map(([currentPage, totalPages]) => {
                const pages = [];
                const maxPagesToShow = 5;
                let startPage = Math.max(
                    1,
                    currentPage - Math.floor(maxPagesToShow / 2)
                );
                const endPage = Math.min(
                    totalPages,
                    startPage + maxPagesToShow - 1
                );

                if (endPage - startPage + 1 < maxPagesToShow) {
                    startPage = Math.max(1, endPage - maxPagesToShow + 1);
                }

                for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                }
                return pages;
            })
        );
    }

    addToCart(event: { productId: string; variantId?: string; quantity: number }) {
        const productId = event.productId;
        const variantId = event.variantId;
        const quantity = event.quantity;
        if (!variantId || quantity <= 0) {
            this.notificationService
                .error(
                    `Invalid product variant or quantity (product ${productId}).`
                )
                .subscribe();
            return;
        }

        this.dataService
            .mutate<AddToCartMutation, AddToCartMutationVariables>(
                ADD_TO_CART,
                {
                    variantId,
                    qty: quantity,
                    ...(this.isConsolidatedFeed
                        ? {
                              customFields: {
                                  isConsolidatedFeedLine: true,
                              },
                          }
                        : {}),
                } as AddToCartMutationVariables,
            )
            .subscribe({
                next: ({ addItemToOrder }) => {
                switch (addItemToOrder.__typename) {
                    case "Order":
                        this.notificationService
                            .success(`Added ${quantity} item(s) to the cart.`)
                            .subscribe();

                        syncConsolidatedFeedOrderFlagFromActiveOrder(
                            this.dataService,
                        )
                            .then(() => this.cartUpdate.next())
                            .catch(() => this.cartUpdate.next());
                        if ("id" in addItemToOrder) {
                            this.stateService.setState(
                                "activeOrderId",
                                addItemToOrder.id
                            );
                        }
                        break;
                    default:
                        if ("message" in addItemToOrder) {
                            const msg = addItemToOrder.message;
                            if (msg && msg.includes("AddingItems")) {
                                this.notificationService
                                    .error(
                                        "You must complete the payment, before adding more items to the cart."
                                    )
                                    .subscribe();
                                this.router.navigate(["/checkout"]);
                            } else {
                                const errorTitle =
                                    msg && msg.includes("insufficient stock")
                                        ? "Insufficient stock"
                                        : "An error occurred";
                                this.notificationService
                                    .notify({
                                        title: errorTitle,
                                        message: msg,
                                        duration: 10000,
                                        type: "error",
                                    })
                                    .subscribe();
                            }
                        } else {
                            this.notificationService
                                .error("Something went wrong.")
                                .subscribe();
                        }
                        break;
                }
                },
                error: (err) => {
                    console.error("AddToCart failed:", err);
                    this.notificationService
                        .error(
                            "Could not add to cart. The request timed out or the server is busy — please try again."
                        )
                        .subscribe();
                },
            });
    }

    downloadAllProducts(format: ExportFormat) {
        const facetValueIds = getRouteArrayParam(
            this.route.snapshot.paramMap,
            "facets",
        );
        const resolvedFacetValueIds = this.getExportFacetValueIds(facetValueIds);
        const term = readSearchTermParam(this.route.snapshot.queryParamMap);
        const supplyParam = this.route.snapshot.queryParamMap.get(
            SUPPLY_TYPE_FILTER_QUERY_PARAM,
        );
        const supplyTypes = supplyParam
            ? supplyParam.split(",").map((v) => v.trim()).filter(Boolean)
            : [];
        const movParam = this.route.snapshot.queryParamMap.get(
            MOV_FILTER_QUERY_PARAM,
        );
        const mov = movParam
            ? movParam.split(",").map((v) => v.trim()).filter(Boolean)
            : [];
        const shippingParam = this.route.snapshot.queryParamMap.get(
            SHIPPING_DATE_FILTER_QUERY_PARAM,
        );
        const shippingDate = shippingParam
            ? shippingParam.split(",").map((v) => v.trim()).filter(Boolean)
            : [];

        const collection = this.latestCollection;
        const isCategoryCollection =
            !!collection?.id &&
            !!this.collectionSlug &&
            this.collectionSlug !== PRODUCTS_COLLECTION_SLUG;
        const hasActiveFilters =
            resolvedFacetValueIds.length > 0 ||
            !!term.trim() ||
            !!this.supplierId ||
            isCategoryCollection ||
            supplyTypes.length > 0 ||
            mov.length > 0 ||
            shippingDate.length > 0;

        const filterTags = this.buildExportFilterTags(
            resolvedFacetValueIds,
            term,
            supplyTypes,
            collection,
            isCategoryCollection,
        );

        this.totalResults$
            .pipe(take(1))
            .subscribe((matchedCount) => {
                this.modalService
                    .fromComponent<
                        CatalogExportModalResult,
                        CatalogExportModalComponent
                    >(CatalogExportModalComponent, {
                        size: "md",
                        closable: true,
                        locals: {
                            format,
                            matchedCount,
                            hasActiveFilters,
                            filterTags,
                            mov: mov[0] ?? null,
                            shippingDate: shippingDate[0] ?? null,
                        },
                    })
                    .pipe(take(1))
                    .subscribe((result) => {
                        if (!result) {
                            return;
                        }

                        const resultMov = result.mov ? [result.mov] : [];
                        const resultShipping = result.shippingDate
                            ? [result.shippingDate]
                            : [];
                        const listingFilters =
                            result.scope === "filtered" && hasActiveFilters
                                ? {
                                      facetValueIds:
                                          resolvedFacetValueIds.length > 0
                                              ? resolvedFacetValueIds
                                              : undefined,
                                      collectionId: isCategoryCollection
                                          ? collection?.id
                                          : undefined,
                                      term:
                                          normalizeEanSearchTerm(term) ||
                                          undefined,
                                      supplyTypes:
                                          supplyTypes.length > 0
                                              ? supplyTypes
                                              : undefined,
                                      supplierId:
                                          this.supplierId || undefined,
                                  }
                                : {};

                        const filters = this.withConsolidatedFeedExportFilters(
                            result.scope === "filtered" ||
                            resultMov.length > 0 ||
                            resultShipping.length > 0
                                ? {
                                      ...listingFilters,
                                      mov:
                                          resultMov.length > 0
                                              ? resultMov
                                              : undefined,
                                      shippingDate:
                                          resultShipping.length > 0
                                              ? resultShipping
                                              : undefined,
                                  }
                                : null,
                        );

                        const hasExportFilters =
                            !!filters &&
                            !!(
                                filters.facetValueIds?.length ||
                                filters.collectionId ||
                                filters.term ||
                                filters.supplyTypes?.length ||
                                filters.supplierId ||
                                filters.supplierIds?.length ||
                                filters.mov?.length ||
                                filters.shippingDate?.length
                            );

                        const date = new Date().toISOString().slice(0, 10);
                        const filenameBase =
                            result.scope === "filtered" || hasExportFilters
                                ? `products-filtered-${date}`
                                : result.includeRetailPrices
                                  ? `products-retail-${date}`
                                  : `products-${date}`;

                        this.exportDownloadService
                            .downloadCatalogShareable(
                                SUPPLY_TYPE.STOCK,
                                filenameBase,
                                result.format,
                                hasExportFilters ? filters : null,
                                result.includeRetailPrices,
                            )
                            .catch((error) => {
                                console.error("Error downloading file:", error);
                                this.notificationService
                                    .error("Error: " + error.message)
                                    .subscribe();
                            });
                    });
            });
    }

    private withConsolidatedFeedExportFilters(
        filters: CatalogExportFilters | null,
    ): CatalogExportFilters | null {
        if (
            !this.isConsolidatedFeed ||
            this.consolidatedFeedPricingIds.length === 0
        ) {
            return filters;
        }
        const supplierIds = this.consolidatedFeedPricingIds.map((id) =>
            String(id),
        );
        if (!filters) {
            return { supplierIds };
        }
        return {
            ...filters,
            supplierIds,
        };
    }

    private getExportFacetValueIds(routeFacetIds: string[]): string[] {
        if (
            this.activeFacetValueIdsFromBrand &&
            !routeFacetIds.includes(this.activeFacetValueIdsFromBrand)
        ) {
            return [...routeFacetIds, this.activeFacetValueIdsFromBrand];
        }
        return routeFacetIds;
    }

    private buildExportFilterTags(
        facetValueIds: string[],
        term: string,
        supplyTypes: string[],
        collection: GetCollectionQuery["collection"],
        isCategoryCollection: boolean,
    ): Array<{ label: string; value: string }> {
        const tags: Array<{ label: string; value: string }> = [];

        if (isCategoryCollection && collection?.name) {
            tags.push({ label: "Category", value: collection.name });
        }

        for (const id of facetValueIds) {
            const facetValue = this.facetValues?.find(
                (fv) => fv.facetValue.id === id,
            );
            if (facetValue) {
                tags.push({
                    label: facetValue.facetValue.facet.name,
                    value: facetValue.facetValue.name,
                });
            } else if (
                this.activeFacetValueIdsFromBrand === id &&
                this.brandDisplayName
            ) {
                tags.push({ label: "Brand", value: this.brandDisplayName });
            }
        }

        for (const type of supplyTypes) {
            const label =
                type === SUPPLY_TYPE.STOCK
                    ? "In Stock"
                    : type === SUPPLY_TYPE.PRE_ORDER
                      ? "Pre-Order"
                      : type;
            tags.push({ label: "Supply Type", value: label });
        }

        if (term.trim()) {
            const eans = extractEanCodesFromText(term);
            if (eans.length > 0) {
                for (const ean of eans) {
                    tags.push({ label: "EAN", value: ean });
                }
            } else {
                tags.push({ label: "Search", value: term.trim() });
            }
        }

        return tags;
    }

    downloadSupplierProducts(format: ExportFormat) {
        const supplierId = this.supplierId;
        if (!supplierId) {
            this.notificationService.error("No supplier selected").subscribe();
            return;
        }

        const date = new Date().toISOString().slice(0, 10);

        this.exportDownloadService
            .downloadSupplierShareable(
                supplierId,
                `Offer #${supplierId} ${date}`,
                format,
            )
            .then(() => {
                this.notificationService
                    .success("Supplier catalog downloaded successfully")
                    .subscribe();
            })
            .catch((error) => {
                console.error("Error downloading supplier file:", error);
                this.notificationService
                    .error("Error: " + error.message)
                    .subscribe();
            });
    }

    shareCatalogLink(format: ExportFormat) {
        this.copyShareLink(() =>
            this.exportDownloadService.copyCatalogShareLink(
                SUPPLY_TYPE.STOCK,
                format,
            ),
        );
    }

    shareSupplierLink(format: ExportFormat) {
        const supplierId = this.supplierId;
        if (!supplierId) {
            this.notificationService.error("No supplier selected").subscribe();
            return;
        }

        this.copyShareLink(() =>
            this.exportDownloadService.copySupplierShareLink(
                supplierId,
                format,
            ),
        );
    }

    private copyShareLink(copyFn: () => Promise<void>) {
        copyFn()
            .then(() => {
                this.notificationService.success(SHARE_LINK_SUCCESS).subscribe();
            })
            .catch((error) => {
                this.notificationService
                    .error(SHARE_LINK_ERROR_PREFIX + error.message)
                    .subscribe();
            });
    }

    getCsvFileStatusText(): string {
        if (!this.csvFileStatus) {
            return "Checking file status...";
        }

        if (this.csvFileStatus.exists && this.csvFileStatus.lastModified) {
            const now = new Date();
            const diffMs =
                now.getTime() - this.csvFileStatus.lastModified.getTime();
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffMinutes = Math.floor(
                (diffMs % (1000 * 60 * 60)) / (1000 * 60)
            );

            if (diffHours > 0) {
                return `Pre-generated catalog available (${diffHours}h ${diffMinutes}m ago)`;
            } else {
                return `Pre-generated catalog available (${diffMinutes}m ago)`;
            }
        } else if (this.csvFileStatus.exists) {
            return "Pre-generated catalog available";
        } else {
            return "Will generate new catalog";
        }
    }

    getProductRoute = (slug: string): string[] => {
        return buildProductRoute(slug, this.supplierId);
    };

    onRequestMoreFacets(): void {
        if (this.facetSkip < this.facetTotalItems) {
            combineLatest([this.collection$, this.searchTerm$])
                .pipe(
                    take(1),
                    switchMap(([collection, term]) =>
                        this.dataService.query<
                            SearchFacetValuesQuery,
                            SearchFacetValuesQueryVariables
                        >(SEARCH_FACET_VALUES, {
                            input: {
                                term,
                                groupByProduct: true,
                                collectionId: collection?.id,
                                ...(this.supplierSearchScope()),
                            },
                            take: this.facetPageSize,
                            skip: this.facetSkip,
                        })
                    ),
                    takeUntil(this.destroy$)
                )
                .subscribe({
                    next: (data) => {
                        const page = data.searchFacetValues;
                        this.facetTotalItems = page.totalItems;
                        const items =
                            page.items as SearchProductsQuery["search"]["facetValues"];
                        this.facetValues = (
                            [
                                ...(this.facetValues || []),
                                ...items,
                            ]
                        ).filter(Boolean);
                        this.facetSkip += this.facetPageSize;
                        if (this.facetValues && this.facetValues.length > 0) {
                            this.facetValuesLoaded$.next(true);
                            this.facetLoadStatus$.next(false);
                        }
                    },
                    error: (error) => {
                        console.error("Error loading more facets:", error);
                        this.facetLoadStatus$.next(false);
                    },
                });
        }
    }

    removeFilter(filter: ActiveFilter) {
        if (filter.type === "facet" && filter.id) {
            const newFacets = this.toggleFacetValueId(filter.id);
            this.router.navigate(
                buildProductListRouteCommands(this.router.url, newFacets),
                {
                    queryParams: { [PAGE_QUERY_PARAM]: 1 },
                    queryParamsHandling: "merge",
                },
            );
        } else if (filter.type === "shipping") {
            this.route.queryParamMap.pipe(take(1)).subscribe((pm) => {
                const current =
                    pm.get(SHIPPING_DATE_FILTER_QUERY_PARAM)?.split(",") || [];
                const next = current.filter((s) => s !== filter.value);
                this.router.navigate(["./"], {
                    queryParams: {
                        [SHIPPING_DATE_FILTER_QUERY_PARAM]:
                            next.length ? next.join(",") : null,
                    },
                    queryParamsHandling: "merge",
                    relativeTo: this.route,
                });
            });
        } else if (filter.type === "mov") {
            this.route.queryParamMap.pipe(take(1)).subscribe((pm) => {
                const current =
                    pm.get(MOV_FILTER_QUERY_PARAM)?.split(",") || [];
                const next = current.filter((m) => m !== filter.value);
                this.router.navigate(["./"], {
                    queryParams: {
                        [MOV_FILTER_QUERY_PARAM]:
                            next.length ? next.join(",") : null,
                    },
                    queryParamsHandling: "merge",
                    relativeTo: this.route,
                });
            });
        } else if (filter.type === "supplyType") {
            this.route.queryParamMap.pipe(take(1)).subscribe((pm) => {
                const current =
                    pm.get(SUPPLY_TYPE_FILTER_QUERY_PARAM)?.split(",") || [];
                const valueToRemove =
                    filter.value === "In Stock"
                        ? SUPPLY_TYPE.STOCK
                        : filter.value === "Pre-Order"
                          ? SUPPLY_TYPE.PRE_ORDER
                          : filter.value.toLowerCase();
                const next = current.filter(
                    (t) => t.trim().toLowerCase() !== valueToRemove,
                );
                this.router.navigate(["./"], {
                    queryParams: {
                        [SUPPLY_TYPE_FILTER_QUERY_PARAM]:
                            next.length ? next.join(",") : null,
                        page: 1,
                    },
                    queryParamsHandling: "merge",
                    relativeTo: this.route,
                });
            });
        }
    }

    clearAllFilters() {
        this.consolidatedFeedSearchTerm = "";
        this.router.navigate(buildProductListRouteCommands(this.router.url), {
            queryParams: getClearProductListFilterQueryParams(),
            queryParamsHandling: "merge",
        });
    }

    toggleFacetValueId(id: string): string[] {
        const existing = (this.route.snapshot.params['facets'] || '').split(',').filter(Boolean);
        return existing.includes(id)
            ? existing.filter((x: string) => x !== id)
            : existing.concat(id);
    }

    private resolveBrandFacet(
        brandSlug: string,
    ): Observable<{ id: string; name: string } | null> {
        const filterTerm = brandSlugToFilterTerm(brandSlug);
        const expectedCode = brandSlugToFacetCode(brandSlug);

        return this.dataService
            .query<{
                catalogBrands: {
                    items: Array<{
                        id: string;
                        name: string;
                        code: string;
                    }>;
                };
            }>(
                GET_CATALOG_BRANDS,
                {
                    take: 100,
                    skip: 0,
                    filterTerm,
                },
                "cache-first",
            )
            .pipe(
                map((result) => {
                    const items = result.catalogBrands?.items ?? [];
                    const match = items.find((fv) => {
                        return (
                            fv.code === expectedCode ||
                            slugifyBrandName(fv.name) === brandSlug
                        );
                    });
                    if (!match) {
                        return null;
                    }
                    return {
                        id: String(match.id),
                        name: match.name,
                    };
                }),
                catchError(() => of(null)),
            );
    }

    openImageModal(imageUrl: string) {
        if (isSupplierNoImagePlaceholder(imageUrl)) {
            return;
        }
        this.zoomedImage = imageUrl;
    }

    hasRealProductImage(product: {
        productAsset?: { preview?: string | null; id?: string | number | null } | null;
    }): boolean {
        return !isSupplierNoImagePlaceholder(
            product.productAsset?.preview,
            product.productAsset?.id,
        );
    }

    closeImageModal() {
        this.zoomedImage = null;
    }

    stopModalContentKeydownBubble(event: KeyboardEvent): void {
        if (event.key !== "Escape") {
            event.stopPropagation();
        }
    }
}

function readSearchTermParam(pm: ParamMap): string {
    const fromTerm = pm
        .getAll(FACET_SEARCH_ROUTE_QUERY_PARAM)
        .filter((value) => !!value);
    if (fromTerm.length) {
        return fromTerm.join(",");
    }
    return pm.getAll(LEGACY_SEARCH_QUERY_PARAM).filter((value) => !!value).join(",");
}
